/**
 * ⚠ The brand guide is written for the marketing site: 56px headlines, 96px section padding, one amber CTA per
 * screen, a footer wave. A dense BI product cannot take those literally — a 96px gap between two charts is not
 * generosity, it is a screen that shows four tiles instead of nine. So this file exposes the **vocabulary**
 * (colour, radius, shadow, type scale, spacing steps) and each consumer picks the step that fits its density.
 * What is applied literally: the palette, Arial, the radii, the navy-tinted shadows, the gradient stripe.
 */

export const BRAND = {
  navy: "#005594",
  navyHover: "#003d6e",
  midBlue: "#466ab1",
  sky: "#76cadd",
  lightBlue: "#c0d9e7",
  ice: "#f1fafc",
  iceHover: "#e8f4f8",
  amber: "#fbc624",
  yellow: "#ffb200",
  orange: "#F16F12",
  red: "#e71f20",
  green: "#1a8040",
  /** Primary body text. Warm near-black, deliberately not pure black */
  text: "#1d1c1d",
  textSecondary: "#666666",
  textMuted: "#888888",
  border: "#e1e1e1",
  white: "#ffffff",
} as const

export const BRAND_GRADIENT =
  "linear-gradient(to right, #466ab1 0%, #76cadd 33%, #fbc624 66%, #e71f20 100%)"

export const BRAND_CTA_GRADIENT = "linear-gradient(to right, #f2c630, #f49926)"
export const BRAND_CTA_GRADIENT_HOVER = "linear-gradient(to right, #f0be20, #f08820)"

export const FONT_STACK = "Arial, Helvetica, sans-serif"

export const FONT_MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"

export const RADIUS = {
  button: 8,
  input: 8,
  card: 12,
  dropdown: 10,
  badge: 6,
  pill: 100,
} as const

export const SHADOW = {
  card: "0 4px 24px rgba(0,85,148,0.13)",
  modal: "0 8px 48px rgba(0,85,148,0.2)",
  dropdown: "0 4px 20px rgba(0,0,0,0.08)",
  focus: "0 0 0 2px rgba(0,85,148,0.15)",
} as const

export const SPACE = { xs: 12, sm: 24, md: 48, lg: 60, xl: 96 } as const

export const TYPE = {
  h1: { size: 56, lineHeight: 62, weight: 700 },
  h2: { size: 46, lineHeight: 50, weight: 400 },
  h3: { size: 34, lineHeight: 42, weight: 600 },
  h4: { size: 24, lineHeight: 30, weight: 400 },
  h5: { size: 18, lineHeight: 24, weight: 700 },
  bodyLarge: { size: 18, lineHeight: 26, weight: 400 },
  body: { size: 16, lineHeight: 24, weight: 400 },
  bodySmall: { size: 14, lineHeight: 20, weight: 400 },
  label: { size: 14, lineHeight: 18, weight: 700, letterSpacing: 0.5, uppercase: true },
  micro: { size: 12, lineHeight: 16, weight: 400 },
} as const

export const TRANSITION = "all .35s"

export const CHART_TOOLTIP_CSS = `box-shadow: ${SHADOW.dropdown}; border-radius: ${RADIUS.dropdown}px;`

export const NAV_HEIGHT = 64
